import { useState, useEffect, useRef } from 'react'
import { CaretDown } from '@telus-uds/palette-allium/build/web/icons'
import { Typography, Icon, FlexGrid, Spacer } from '@telus-uds/components-web'
import ButtonLink, { ButtonLinkProps } from '../../core/ButtonLink/ButtonLink'
import {
  BlackGradient,
  VideoContainer,
  Video,
  CaretContainer,
  StreamingServiceContainer,
  ZeroOpacity,
  TrailerImage,
  StreamingLogo,
  MainTitleContainer,
  MainTextContainer,
} from './Styles'
import { gsap } from 'gsap/dist/gsap'

type AnimatedTrailerProps = {
  tileHeight: number
  isReducedMotionOn: boolean
  streamingLogoText: string
  trailerFirstText: string
  trailerSecondText: string
  trailerSubText: string
  streamingLogo: string
  trailerVideo: string
  trailerVideoFallback: string
  staticBackground: string
  videoGradientDarkness: number
  buttonLinkProps: ButtonLinkProps
}
const AnimatedTrailer = ({
  tileHeight,
  isReducedMotionOn,
  streamingLogoText,
  trailerFirstText,
  trailerSecondText,
  trailerSubText,
  streamingLogo,
  trailerVideo,
  trailerVideoFallback,
  staticBackground,
  videoGradientDarkness,
  buttonLinkProps,
}: AnimatedTrailerProps) => {
  const blackGradientRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const animatedTrailerRef = useRef<HTMLDivElement>(null)
  const sentenceOneRef = useRef<HTMLDivElement>(null)
  const sentenceTwoRef = useRef<HTMLDivElement>(null)
  const sentenceThreeRef = useRef<HTMLDivElement>(null)
  const streamingLogoRef = useRef<HTMLDivElement>(null)
  const streamingTextRef = useRef<HTMLDivElement>(null)
  const mainTitleRef = useRef<HTMLDivElement>(null)

  const [videoHeight, setVideoHeight] = useState(0)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    setVideoHeightHelper()

    const MOBILE = 768

    const resizeObserver = new ResizeObserver(() => {
      // min height to ensure all text is visible in video
      const minHeight = mainTitleRef.current.getBoundingClientRect().height + 200
      setVideoHeight(
        Math.max(
          window.innerHeight - animatedTrailerRef.current.getBoundingClientRect().top - window.scrollY,
          minHeight
        )
      )
      if (window.document.body.clientWidth < MOBILE) {
        setIsMobile(true)
      } else {
        setIsMobile(false)
      }
    })

    if (document) {
      resizeObserver.observe(document.body)
    }
  }, [])

  useEffect(() => {
    const refs = [
      blackGradientRef.current,
      sentenceOneRef.current,
      sentenceTwoRef.current,
      sentenceThreeRef.current,
      streamingLogoRef.current,
      streamingTextRef.current,
    ]
    const tl = gsap.timeline()
    if (!isReducedMotionOn) {
      const gsapTimeline = () => {
        const gradient = refs.shift()
        tl.to(gradient, { opacity: videoGradientDarkness / 100, duration: 1 }, 1)
        refs.forEach((ref) => {
          tl.to(ref, { opacity: 1, duration: 0.5 }, '>')
        })
      }
      videoRef.current.removeEventListener('play', gsapTimeline)
      // play listener cb not triggereed if video has started, so fn if started
      videoRef.current.currentTime > 0 && gsapTimeline()
      videoRef.current.addEventListener('play', gsapTimeline)
    }
    return () => {
      tl.kill()
    }
  }, [videoRef])

  const setVideoHeightHelper = () => {
    if (animatedTrailerRef.current && window) {
      setVideoHeight(window.innerHeight - animatedTrailerRef.current.getBoundingClientRect().top - window.scrollY)
    }
  }

  return (
    <div ref={animatedTrailerRef}>
      <VideoContainer height={videoHeight}>
        {!isReducedMotionOn && (
          <Video ref={videoRef} loop autoPlay muted playsInline loading="lazy" width="100%" height="100%">
            <source src={trailerVideo} type="video/webm" />
            <source src={trailerVideoFallback} type="video/mp4" />
          </Video>
        )}
        {isReducedMotionOn && (
          <TrailerImage src={staticBackground} alt="" style={{ width: '100%', height: '100%', top: '0%' }} />
        )}
        <MainTextContainer initialOpacity={isReducedMotionOn ? 1 : 0.1}>
          <div ref={mainTitleRef}>
            <MainTitleContainer>
              <FlexGrid.Row horizontalAlign="center">
                <ZeroOpacity ref={sentenceOneRef} opacity={isReducedMotionOn ? 1 : 0.1}>
                  <Typography block variant={{ size: 'display1', inverse: true }} heading="h1">
                    {trailerFirstText}
                  </Typography>
                </ZeroOpacity>
                <ZeroOpacity ref={sentenceTwoRef} opacity={isReducedMotionOn ? 1 : 0.1}>
                  <Typography
                    block
                    tokens={{ fontWeight: 700 }}
                    variant={{ size: 'display1', inverse: true, bold: true }}
                    heading="h1"
                  >
                    {trailerSecondText}
                  </Typography>
                </ZeroOpacity>
              </FlexGrid.Row>
              <FlexGrid.Col horizontalAlign="center">
                <ZeroOpacity ref={sentenceThreeRef} opacity={isReducedMotionOn ? 1 : 0.1}>
                  <Typography block variant={{ size: 'large', inverse: true }}>
                    {trailerSubText}
                  </Typography>
                </ZeroOpacity>
              </FlexGrid.Col>
              {buttonLinkProps && (
                <FlexGrid.Col horizontalAlign="center">
                  <Spacer space={3} />
                  <ButtonLink {...buttonLinkProps} />
                </FlexGrid.Col>
              )}
            </MainTitleContainer>
          </div>

          <StreamingServiceContainer tileHeight={tileHeight}>
            <ZeroOpacity ref={streamingLogoRef} opacity={isReducedMotionOn ? 1 : 0.1} style={{ textAlign: 'right' }}>
              <StreamingLogo src={streamingLogo} alt={''} />
            </ZeroOpacity>
            <ZeroOpacity ref={streamingTextRef} opacity={isReducedMotionOn ? 1 : 0.1}>
              <Typography block align="right" variant={{ size: 'small', inverse: true }}>
                {streamingLogoText}
              </Typography>
            </ZeroOpacity>
          </StreamingServiceContainer>
          {!isMobile && (
            <CaretContainer tileHeight={tileHeight}>
              <Icon icon={CaretDown} tokens={{ size: '36px' }} variant={{ size: 'medium', color: 'inverse' }} />
            </CaretContainer>
          )}
        </MainTextContainer>
        <BlackGradient ref={blackGradientRef} trailer={true} />
      </VideoContainer>
    </div>
  )
}

export default AnimatedTrailer
